import { useState, useEffect, useRef, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import axios from "axios";
import { detectToolsOrigin } from "../../../utils/helper.js";

import {
  Check,
  AlertCircle,
  TrendingUp,
  DollarSign,
  Home,
  Info,
  Share2,
  Twitter,
  Facebook,
  Linkedin,
  ArrowRight,
} from "lucide-react";

import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";

// ✅ SAME AS OTHERS: proxy url
const PROXY_URL =
  window.__UK_PROXY_URL__ || `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

const formatCurrency = (val) => {
  const n = Number(val) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
};

const formatPercent = (val) => {
  const n = Number(val) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(n);
};

// ✅ Keep disclaimer simple (you can tweak copy anytime)
const homeOfficeDisclaimerText = (year) =>
  `This calculator provides estimates based on ${year} tax rules. Actual tax liability may vary based on your specific situation, deductions, and other factors. Consult Uncle Kam's tax professionals for personalized advice.`;

export default function HomeOfficeCalculator({
  year = 2025,
  calculatorName = "Home Office Deduction Calculator",
  endpoint = PROXY_URL, // ✅ now defaults to proxy
  redirectUrl = "https://unclekam.com/contact-us/",
  pageUrlProp,
}) {
  const { register, control, setValue } = useForm({
    defaultValues: {
      officeSqft: 150,
      totalHomeSqft: 2000,
      mortgageInterest: 0,
      propertyTaxes: 0,
      insurance: 0,
      utilities: 0,
      repairs: 0,
      ownHome: "yes",
      homeValue: 0,
    },
  });

  const values = useWatch({ control });

  // ✅ results should only update on button click
  const [results, setResults] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  // ✅ keep last computed JSON payload for emailing (same as others)
  const latestPayloadRef = useRef({ inputs_json: null, results_json: null });

  // share dropdown
  const [shareOpen, setShareOpen] = useState(false);
  const dropdownRef = useRef(null);

  // results scroll anchor
  const resultsRef = useRef(null);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => params.has("qa_mode"), [params]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setShareOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // ====== Gate storage (24h) ======
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

  const readUnlock = () => {
    try {
      const raw = localStorage.getItem(UNK_GATE_KEY);
      if (!raw) return { unlocked: false, email: "" };
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const email = String(parsed?.email || "");
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(UNK_GATE_KEY);
        return { unlocked: false, email: "" };
      }
      return { unlocked: true, email };
    } catch {
      return { unlocked: false, email: "" };
    }
  };

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  const pageUrl =
    pageUrlProp || (typeof window !== "undefined" ? window.location.href : "");

  // gate state
  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  // modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  // ✅ if user changes any input after calculating, invalidate results + payload
  useEffect(() => {
    setHasCalculated(false);
    setResults(null);
    latestPayloadRef.current = { inputs_json: null, results_json: null };
  }, [
    values?.officeSqft,
    values?.totalHomeSqft,
    values?.mortgageInterest,
    values?.propertyTaxes,
    values?.insurance,
    values?.utilities,
    values?.repairs,
    values?.ownHome,
    values?.homeValue,
    year,
  ]);

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    writeUnlock({ email });
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ SAME AS OTHERS: proxy endpoint + pdf_notes.disclaimer
  const sendResultsToEmail = async (emailToSend) => {
    const payload = latestPayloadRef.current;
    if (!payload?.inputs_json || !payload?.results_json) return null;

    setSendingResults(true);
    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: String(year),
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: payload.inputs_json,
            results_json: payload.results_json,
            pdf_notes: {
              disclaimer: homeOfficeDisclaimerText(year),
            },
            schema_version: 1,
          },
        },
        { headers: { "Content-Type": "application/json" }, timeout: 30000 }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();
      return resp.data;
    } catch (e) {
      console.error("sendResultsToEmail error:", e);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleChangeEmail = () => {
    setShowConfirmModal(false);
    setShowUpdateEmailModal(true);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    writeUnlock({ email: newEmail });
    setStoredEmail(newEmail);
    await sendResultsToEmail(newEmail);
  };

  // ✅ compute-only helper
  const computePayload = (data) => {
    const officeSqft = Number(data.officeSqft) || 0;
    const totalHomeSqft = Number(data.totalHomeSqft) || 1;
    const ownHome = data.ownHome === "yes" ? "yes" : "no";

    const businessUsePercent = Math.min(officeSqft / totalHomeSqft, 1);

    const mortgageInterest = Number(data.mortgageInterest) || 0;
    const propertyTaxes = Number(data.propertyTaxes) || 0;
    const insurance = Number(data.insurance) || 0;
    const utilities = Number(data.utilities) || 0;
    const repairs = Number(data.repairs) || 0;

    const directExpenses =
      mortgageInterest + propertyTaxes + insurance + utilities + repairs;

    const deductibleDirect = directExpenses * businessUsePercent;

    let deductibleDepreciation = 0;
    if (ownHome === "yes") {
      const homeValue = Number(data.homeValue) || 0;
      const specAnnualDepreciation = (homeValue * 0.8) / 27.5;
      deductibleDepreciation = specAnnualDepreciation * businessUsePercent;
    }

    const totalActualDeduction = deductibleDirect + deductibleDepreciation;

    const simplifiedDeduction = Math.min(officeSqft, 300) * 5;

    // we display savings everywhere using 24% assumption (same as your previous)
    const assumedMarginalRate = 0.24;
    const simplifiedSavings = simplifiedDeduction * assumedMarginalRate;
    const actualSavings = totalActualDeduction * assumedMarginalRate;

    const betterMethod =
      totalActualDeduction > simplifiedDeduction
        ? "Actual Expenses"
        : "Simplified Method";

    const maxDeduction = Math.max(totalActualDeduction, simplifiedDeduction);
    const minDeduction = Math.min(totalActualDeduction, simplifiedDeduction);
    const additionalSavings = (maxDeduction - minDeduction) * assumedMarginalRate;

    // ✅ ONLY what is displayed on screen
    const inputs_json = {
      officeSqft,
      totalHomeSqft,
      ownHome,
      ...(ownHome === "yes" ? { homeValue: Number(data.homeValue) || 0 } : {}),
      mortgageInterest,
      propertyTaxes,
      insurance,
      utilities,
      repairs,
      year: Number(year),
      calculatorName,
    };

    const results_json = {
      recommendation: {
        betterMethod,
        additionalSavings,
      },
      simplified: {
        deduction: simplifiedDeduction,
        savings: simplifiedSavings,
      },
      actual: {
        percent: businessUsePercent,
        directDeduction: deductibleDirect,
        depreciationDeduction: deductibleDepreciation,
        totalDeduction: totalActualDeduction,
        savings: actualSavings,
      },
    };

    return { inputs_json, results_json };
  };

  const handleViewResults = () => {
    const payload = computePayload(values || {});
    latestPayloadRef.current = payload;

    setResults(payload.results_json);
    setHasCalculated(true);

    openGateFlow();
    setTimeout(() => scrollToResults(), 50);
  };

  // ✅ if they click overlay before calc, calc first so preview becomes real
  const runCalcIfPossible = () => {
    const payload = computePayload(values || {});
    latestPayloadRef.current = payload;

    setResults(payload.results_json);
    setHasCalculated(true);
  };

  const isOwnHomeYes = values?.ownHome === "yes";
  const betterIsSimplified = results?.recommendation?.betterMethod === "Simplified Method";
  const betterIsActual = results?.recommendation?.betterMethod === "Actual Expenses";

  return (
    <div className="no-focus-ui">
      <style>{`
        .no-focus-ui .form-control:focus,
        .no-focus-ui .form-select:focus,
        .no-focus-ui textarea.form-control:focus,
        .no-focus-ui input.form-control:focus,
        .no-focus-ui .input-group .form-control:focus,
        .no-focus-ui .input-group .form-select:focus {
          outline: none !important;
          box-shadow: none !important;
          border-color: #ced4da !important;
        }
        .no-focus-ui .btn:focus,
        .no-focus-ui .btn:focus-visible,
        .no-focus-ui .dropdown-toggle:focus,
        .no-focus-ui .dropdown-toggle:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
        .no-focus-ui *:focus-visible { outline: none !important; }

        /* ===== Results lock system (same as other calculators) ===== */
        .unk-results-wrap { position: relative; }
        .unk-results-blur { filter: blur(10px); user-select: none; pointer-events: none; }

        .unk-bg-blur {
          position: absolute;
          inset: -30px;
          background:
            radial-gradient(circle at 20% 20%, rgba(15,138,76,0.18), transparent 55%),
            radial-gradient(circle at 80% 70%, rgba(246,184,32,0.16), transparent 55%),
            linear-gradient(180deg, rgba(255,255,255,0.90), rgba(255,255,255,0.90));
          filter: blur(18px);
          transform: scale(1.05);
          z-index: 0;
          pointer-events: none;
        }

        .unk-results-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          text-align: center;
          background: rgba(255,255,255,0.25);
          backdrop-filter: blur(16px) saturate(140%);
          -webkit-backdrop-filter: blur(16px) saturate(140%);
          border-radius: 1rem;
          cursor: pointer;
          z-index: 10;
          box-shadow: inset 0 0 0 1px rgba(0,0,0,0.06);
        }

        .unk-overlay-card {
          max-width: 360px;
          width: 100%;
          border-radius: 14px;
          background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          border: 1px solid rgba(0,0,0,0.06);
          padding: 18px 16px;
        }

        .unk-overlay-title { font-weight: 800; font-size: 18px; margin-bottom: 6px; }
        .unk-overlay-body { color: #6c757d; font-size: 14px; margin-bottom: 12px; }

        .unk-overlay-btn {
          background: #0f8a4c;
          border: 1px solid #0f8a4c;
          color: white;
          width: 100%;
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 700;
        }
      `}</style>

      <div className="container py-4 py-md-5" style={{ maxWidth: 1100 }}>
        {/* Header */}
        <div className="text-center mb-4 mb-md-5">
          <h1 className="fw-bold mb-2" style={{ color: "#1E1E5E" }}>
            Home Office Deduction Calculator
          </h1>
          <div
            className="mx-auto rounded-pill mb-3"
            style={{ width: 96, height: 4, background: "#F6B820" }}
          />
          <p className="text-muted fs-5 mb-0" style={{ maxWidth: 720, margin: "0 auto" }}>
            Compare the Simplified Method vs. Actual Expenses to find your biggest tax write-off.
          </p>
        </div>

        <div className="row g-4">
          {/* LEFT: INPUTS */}
          <div className="col-12 col-lg-7">
            {/* Property Details */}
            <div className="card border-2 rounded-4 border-gray-300 mb-4">
              <div className="card-header border-0 rounded-4 bg-white py-3 px-0">
                <div
                  className="d-flex align-items-center gap-2 px-4 py-4"
                  style={{ background: "#f8f9fa" }}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded"
                    style={{ width: 28, height: 28, background: "#E0E7FF", color: "#5858FF" }}
                  >
                    <Home size={18} />
                  </span>
                  <div className="fw-bold" style={{ color: "#1E1E5E" }}>
                    Property Details
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold text-muted">Office Area</label>
                    <div className="d-flex align-items-center justify-content-end border border-2 rounded-3 px-2">
                      <input
                        type="number"
                        className="border-0 outline-0 w-100"
                        {...register("officeSqft", { valueAsNumber: true })}
                        style={{ height: 44 }}
                      />
                      <span className="text-end fw-semibold" style={{ width: 40, color: "#99a1af" }}>
                        sq ft
                      </span>
                    </div>
                  </div>

                  <div className="col-6">
                    <label className="form-label fw-semibold text-muted">Total Home Area</label>
                    <div className="d-flex align-items-center justify-content-end border border-2 rounded-3 px-2">
                      <input
                        type="number"
                        className="border-0 outline-0 w-100"
                        {...register("totalHomeSqft", { valueAsNumber: true })}
                        style={{ height: 44 }}
                      />
                      <span className="text-end fw-semibold" style={{ width: 40, color: "#99a1af" }}>
                        sq ft
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="form-label fw-semibold text-muted mb-2">Do you own your home?</div>

                  <div className="d-flex flex-column flex-sm-row gap-2">
                    <input
                      type="radio"
                      className="btn-check"
                      name="ownHomeRadio"
                      id="ownHomeYes"
                      checked={values?.ownHome === "yes"}
                      onChange={() => setValue("ownHome", "yes")}
                    />
                    <label
                      className="btn d-flex align-items-center justify-content-center gap-2"
                      htmlFor="ownHomeYes"
                      style={{
                        borderColor: values?.ownHome === "yes" ? "#F6B820" : "#dee2e6",
                        background: values?.ownHome === "yes" ? "#FFFBEB" : "#fff",
                        color: "#1E1E5E",
                        fontWeight: 600,
                        borderWidth: 2,
                      }}
                    >
                      <span
                        className="rounded-circle d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: 16,
                          height: 16,
                          border: "2px solid #F6B820",
                          background: values?.ownHome === "yes" ? "#F6B820" : "transparent",
                        }}
                      />
                      Yes, I own
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="ownHomeRadio"
                      id="ownHomeNo"
                      checked={values?.ownHome === "no"}
                      onChange={() => setValue("ownHome", "no")}
                    />
                    <label
                      className="btn d-flex align-items-center justify-content-center gap-2"
                      htmlFor="ownHomeNo"
                      style={{
                        borderColor: values?.ownHome === "no" ? "#F6B820" : "#dee2e6",
                        background: values?.ownHome === "no" ? "#FFFBEB" : "#fff",
                        color: "#1E1E5E",
                        fontWeight: 600,
                        borderWidth: 2,
                      }}
                    >
                      <span
                        className="rounded-circle d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: 16,
                          height: 16,
                          border: "2px solid #F6B820",
                          background: values?.ownHome === "no" ? "#F6B820" : "transparent",
                        }}
                      />
                      No, I rent
                    </label>
                  </div>

                  {isOwnHomeYes && (
                    <div className="mt-3 p-3 rounded border" style={{ background: "#f8f9fa" }}>
                      <label className="form-label fw-semibold text-muted d-flex align-items-center gap-2">
                        Home Value (Cost Basis)
                        <Info size={16} className="text-muted" />
                      </label>

                      <div className="border px-2 border-2 bg-white d-flex align-items-center flex-row rounded-3">
                        <span className="px-2 text-semibold" style={{ color: "#99a1af" }}>
                          $
                        </span>
                        <input
                          type="number"
                          className="w-100 border-0"
                          {...register("homeValue", { valueAsNumber: true })}
                          style={{ height: 44 }}
                        />
                      </div>

                      <div className="small text-muted mt-1">
                        Used to calculate your depreciation deduction.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Annual Expenses */}
            <div className="card border-2 rounded-4 border-gray-300 mb-4">
              <div className="card-header border-0 rounded-4 bg-white py-3 px-0">
                <div
                  className="d-flex align-items-center gap-2 px-4 py-4"
                  style={{ background: "#f8f9fa" }}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded"
                    style={{ width: 28, height: 28, background: "#E0E7FF", color: "#5858FF" }}
                  >
                    <DollarSign size={18} />
                  </span>
                  <div className="fw-bold" style={{ color: "#1E1E5E" }}>
                    Annual Expenses
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                {[
                  { label: isOwnHomeYes ? "Mortgage Interest" : "Annual Rent", name: "mortgageInterest" },
                  { label: "Property Taxes", name: "propertyTaxes" },
                  { label: "Insurance", name: "insurance" },
                  { label: "Utilities (Gas, Electric, Water)", name: "utilities" },
                  { label: "Repairs & Maintenance", name: "repairs" },
                ].map((field) => (
                  <div key={field.name} className="row align-items-center g-2 mb-3">
                    <div className="col-12 col-sm-7">
                      <label className="form-label mb-0 text-muted fw-semibold">
                        {field.label}
                      </label>
                    </div>
                    <div className="col-12 col-sm-5">
                      <div className="d-flex align-items-center justify-content-end border border-2 rounded-3 px-2">
                        <span className="fw-semibold" style={{ width: "40px", color: "#99a1af" }}>
                          $
                        </span>
                        <input
                          type="number"
                          className="border-0 outline-0 w-100 fw-semibold text-end"
                          placeholder="0"
                          {...register(field.name, { valueAsNumber: true })}
                          style={{ height: 40 }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* VIEW RESULTS */}
            <div className="d-flex justify-content-center justify-content-lg-start">
              <button
                type="button"
                className="btn fw-bold d-inline-flex align-items-center gap-2"
                style={{
                  background: "#0f8a4c",
                  borderColor: "#0f8a4c",
                  color: "white",
                  padding: "12px 18px",
                  borderRadius: 12,
                  width: "100%",
                  maxWidth: 420,
                }}
                onClick={handleViewResults}
              >
                View Results
                <ArrowRight size={18} />
              </button>
            </div>

            <div className="text-muted small mt-2">
              Results will only calculate after clicking <strong>View Results</strong>.
            </div>
          </div>

          {/* RIGHT: RESULTS (always visible, locked/blurred until unlocked) */}
          <div className="col-12 col-lg-5" ref={resultsRef}>
            <div className="unk-results-wrap" style={{ borderRadius: 14, overflow: "hidden" }}>
              {!unlocked && <div className="unk-bg-blur" />}

              <div style={{ position: "relative", zIndex: 1 }}>
                <div className={!unlocked ? "unk-results-blur" : ""}>
                  {/* Recommendation */}
                  <div
                    className="card border-0 shadow-sm mb-4"
                    style={{
                      background: "#1E1E5E",
                      color: "white",
                      borderRadius: 14,
                    }}
                  >
                    <div className="card-body text-center p-4 p-md-5">
                      <div className="text-uppercase fw-bold small mb-3" style={{ color: "#BFD3FF" }}>
                        Recommended Strategy
                      </div>

                      <div className="fw-bold mb-2" style={{ fontSize: 34, lineHeight: 1.1 }}>
                        {hasCalculated && results ? results.recommendation.betterMethod : "—"}
                      </div>

                      <div
                        className="d-flex align-items-center justify-content-center gap-2 fw-semibold mb-3"
                        style={{ color: "#F6B820" }}
                      >
                        <TrendingUp size={18} />
                        <span>
                          Save an extra{" "}
                          {formatCurrency(hasCalculated && results ? results.recommendation.additionalSavings : 0)}{" "}
                          / year
                        </span>
                      </div>

                      <div
                        className="rounded p-3"
                        style={{
                          background: "rgba(255,255,255,0.10)",
                          border: "1px solid rgba(255,255,255,0.12)",
                        }}
                      >
                        <div className="small" style={{ color: "#EAF2FF" }}>
                          {!hasCalculated
                            ? "Fill inputs and click “View Results” to calculate."
                            : results.recommendation.betterMethod === "Actual Expenses"
                            ? "Based on your inputs, itemizing yields a larger deduction than the simplified rate."
                            : "The Simplified Method is better for you. It saves you time and provides a larger deduction."}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Simplified */}
                  <div
                    className="card shadow-sm mb-3"
                    style={{
                      borderRadius: 14,
                      border: betterIsSimplified ? "2px solid #F6B820" : "1px solid #e9ecef",
                      background: betterIsSimplified ? "#fff" : "#f8f9fa",
                      opacity: betterIsSimplified ? 1 : 0.9,
                    }}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="fw-bold d-flex align-items-center gap-2" style={{ color: "#1E1E5E" }}>
                          Simplified Method
                          {betterIsSimplified && <Check size={18} style={{ color: "#F6B820" }} />}
                        </div>
                        <span className="badge bg-light border" style={{ color: "#6c757d" }}>
                          Easy
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-end">
                        <div className="text-muted small">Total Deduction</div>
                        <div className="fw-bold" style={{ fontSize: 28, color: "#1E1E5E" }}>
                          {formatCurrency(hasCalculated && results ? results.simplified.deduction : 0)}
                        </div>
                      </div>
                      <div className="text-muted small text-end mt-1">
                        Est. Tax Savings:{" "}
                        {formatCurrency(hasCalculated && results ? results.simplified.savings : 0)}
                      </div>
                    </div>
                  </div>

                  {/* Actual */}
                  <div
                    className="card shadow-sm mb-3"
                    style={{
                      borderRadius: 14,
                      border: betterIsActual ? "2px solid #F6B820" : "1px solid #e9ecef",
                      background: betterIsActual ? "#fff" : "#f8f9fa",
                      opacity: betterIsActual ? 1 : 0.9,
                    }}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="fw-bold d-flex align-items-center gap-2" style={{ color: "#1E1E5E" }}>
                          Actual Expenses
                          {betterIsActual && <Check size={18} style={{ color: "#F6B820" }} />}
                        </div>
                        <span className="badge bg-light border" style={{ color: "#6c757d" }}>
                          Advanced
                        </span>
                      </div>

                      <div className="d-flex justify-content-between small mb-2">
                        <span className="text-muted">Business Use</span>
                        <span className="fw-semibold" style={{ color: "#5858FF" }}>
                          {formatPercent(hasCalculated && results ? results.actual.percent : 0)}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between small mb-2">
                        <span className="text-muted">Direct Expenses</span>
                        <span className="fw-semibold">
                          {formatCurrency(hasCalculated && results ? results.actual.directDeduction : 0)}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between small mb-3">
                        <span className="text-muted">Depreciation</span>
                        <span className="fw-semibold">
                          {formatCurrency(hasCalculated && results ? results.actual.depreciationDeduction : 0)}
                        </span>
                      </div>

                      <hr className="my-3" />

                      <div className="d-flex justify-content-between align-items-end">
                        <div className="text-muted small fw-semibold">Total Deduction</div>
                        <div className="fw-bold" style={{ fontSize: 28, color: "#1E1E5E" }}>
                          {formatCurrency(hasCalculated && results ? results.actual.totalDeduction : 0)}
                        </div>
                      </div>
                      <div className="text-muted small text-end mt-1">
                        Est. Tax Savings:{" "}
                        {formatCurrency(hasCalculated && results ? results.actual.savings : 0)}
                      </div>
                    </div>
                  </div>

                  {/* Warning */}
                  <div
                    className="alert d-flex gap-2 align-items-start mb-4"
                    role="alert"
                    style={{
                      background: "#EFF6FF",
                      borderColor: "#DBEAFE",
                      borderRadius: 14,
                      color: "#1E3A8A",
                    }}
                  >
                    <AlertCircle size={18} style={{ color: "#5858FF", marginTop: 2 }} />
                    <div>
                      <div className="fw-bold mb-1" style={{ color: "#1E1E5E" }}>
                        Depreciation Recapture Warning
                      </div>
                      <div className="small">
                        If you claim depreciation (Actual Method), you may owe tax on that amount when you sell your
                        home. The Simplified Method avoids this.
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    type="button"
                    className="btn w-100 fw-bold d-flex align-items-center justify-content-center gap-2 mb-3"
                    style={{
                      background: "#1E1E5E",
                      color: "white",
                      padding: "14px 16px",
                      borderRadius: 12,
                    }}
                    onClick={() => window.open(redirectUrl, "_blank", "noopener,noreferrer")}
                  >
                    Get Professional Tax Help
                    <ArrowRight size={18} />
                  </button>

                  {/* Share */}
                  <div className="d-flex justify-content-center" ref={dropdownRef}>
                    <div className="position-relative">
                      <button
                        type="button"
                        className="btn fw-bold d-inline-flex align-items-center gap-2"
                        style={{
                          borderRadius: 12,
                          border: "2px solid #F6B820",
                          color: "#1E1E5E",
                          background: "#fff",
                          padding: "10px 14px",
                        }}
                        onClick={() => setShareOpen((v) => !v)}
                      >
                        <Share2 size={16} />
                        Share My Savings
                      </button>

                      {shareOpen && (
                        <div
                          className="dropdown-menu show p-2 shadow"
                          style={{
                            width: 260,
                            borderRadius: 14,
                            border: "1px solid #e9ecef",
                            left: "50%",
                            transform: "translateX(-50%)",
                            top: "calc(100% + 10px)",
                            position: "absolute",
                          }}
                        >
                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center gap-2 rounded py-2"
                            onClick={() => {
                              setShareOpen(false);
                              const text = `I just found ${formatCurrency(
                                results?.recommendation?.additionalSavings || 0
                              )} in extra tax savings with the Uncle Kam Home Office Calculator! Check it out:`;
                              const url = pageUrl || "https://unclekam.com";
                              window.open(
                                `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
                                  url
                                )}`,
                                "_blank"
                              );
                            }}
                          >
                            <span
                              className="d-inline-flex align-items-center justify-content-center rounded"
                              style={{ width: 26, height: 26, background: "#000", color: "#fff" }}
                            >
                              <Twitter size={14} />
                            </span>
                            <span className="fw-semibold text-muted">Share on X</span>
                          </button>

                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center gap-2 rounded py-2"
                            onClick={() => {
                              setShareOpen(false);
                              const url = pageUrl || "https://unclekam.com";
                              window.open(
                                `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
                                "_blank"
                              );
                            }}
                          >
                            <span
                              className="d-inline-flex align-items-center justify-content-center rounded"
                              style={{ width: 26, height: 26, background: "#1877F2", color: "#fff" }}
                            >
                              <Facebook size={14} />
                            </span>
                            <span className="fw-semibold text-muted">Share on Facebook</span>
                          </button>

                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center gap-2 rounded py-2"
                            onClick={() => {
                              setShareOpen(false);
                              const url = pageUrl || "https://unclekam.com";
                              window.open(
                                `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
                                "_blank"
                              );
                            }}
                          >
                            <span
                              className="d-inline-flex align-items-center justify-content-center rounded"
                              style={{ width: 26, height: 26, background: "#0A66C2", color: "#fff" }}
                            >
                              <Linkedin size={14} />
                            </span>
                            <span className="fw-semibold text-muted">Share on LinkedIn</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* UI Disclaimer */}
                  <div className="text-muted small text-center mt-4">
                    * {homeOfficeDisclaimerText(year)}
                  </div>
                </div>

                {/* ✅ overlay if locked (ALWAYS when not unlocked) */}
                {!unlocked && (
                  <div
                    className="unk-results-overlay"
                    onClick={() => {
                      if (!hasCalculated) runCalcIfPossible();
                      openGateFlow();
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        if (!hasCalculated) runCalcIfPossible();
                        openGateFlow();
                      }
                    }}
                    aria-label="Results locked. Open unlock modal."
                  >
                    <div className="unk-overlay-card">
                      <div className="unk-overlay-title">Results Locked</div>
                      <div className="unk-overlay-body">
                        Enter your name and email to view your results instantly.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* helper note */}
            {!hasCalculated && (
              <div className="text-center text-muted small mt-2">
                Fill inputs and click <strong>View Results</strong> to calculate.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ MODALS (same as others: pass payloads so email/pdf contains correct content) */}
      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => handleLeadSuccess(payload)}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        endpoint={endpoint}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={latestPayloadRef.current?.inputs_json}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={handleChangeEmail}
        sending={sendingResults}
      />

      <UpdateEmailModal
        open={showUpdateEmailModal}
        initialEmail={storedEmail}
        onClose={() => setShowUpdateEmailModal(false)}
        onSend={handleUpdateEmailAndSend}
        sending={sendingResults}
        storedEmail={storedEmail}
        endpoint={PROXY_URL}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={latestPayloadRef.current?.inputs_json}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />
    </div>
  );
}
